import React, { useReducer, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { AuthDispatchContext, AuthPermissionsContext, AuthStateContext } from '../authContext/AuthContext'
import {
  authReducer,
  SET_AUTHORISED,
  SET_UNAUTHORISED,
  SET_PERMISSIONS,
  SET_PERMISSIONS_ERROR,
  initialState,
  initializer
} from '../authReducer/authReducer'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AuthContextControllerProps } from './AuthContextController.types'
import { logger } from '@percent/admin-dashboard/common/utility/logger'
import { useFetchPermissions } from '@percent/admin-dashboard/common/hooks/useFetchPermissions'
import { fetchPermissions } from '@percent/admin-dashboard/common/hooks/useFetchPermissions/fetchPermissions'

export function AuthContextController({ children }: AuthContextControllerProps) {
  const [state, dispatch] = useReducer(authReducer, initialState, initializer)
  const { authService, iamService } = useServices()
  const queryClient = useQueryClient()
  const wasAuthorised = useRef(state.isAuthorised)

  useFetchPermissions(state, dispatch)

  // Signing out happens here, whatever ended the session (the logout menu, a
  // 401, an account that no longer loads): the stored auth state goes, and so
  // does every cached query, since the query client outlives the session and
  // would otherwise show the next admin on this tab the previous one's data.
  useEffect(() => {
    if (wasAuthorised.current && !state.isAuthorised) {
      localStorage.removeItem('authState')
      queryClient.clear()
    }
    wasAuthorised.current = state.isAuthorised
  }, [state.isAuthorised, queryClient])

  useEffect(() => {
    const getUserData = async () => {
      try {
        const {
          data: { data }
        } = await authService.getAccount()

        dispatch({
          type: SET_AUTHORISED,
          user: state.user ?? data
        })

        try {
          const permissions = await fetchPermissions(iamService)

          dispatch({
            type: SET_PERMISSIONS,
            permissions
          })
        } catch (permissionsError) {
          logger(permissionsError as Error)
          dispatch({ type: SET_PERMISSIONS_ERROR })
        }
      } catch {
        dispatch({
          type: SET_UNAUTHORISED
        })
      }
    }

    if (state.isAuthorised && !state.isAuthorising) {
      getUserData().catch(logger)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state.isAuthorised, state.isAuthorising, authService, iamService])

  const permissionsContextValue = useMemo(
    () => ({
      permissions: state.permissions,
      permissionsSet: state.permissionsSet,
      shouldRefetchPermissions: state.shouldRefetchPermissions
    }),
    [state.permissions, state.permissionsSet, state.shouldRefetchPermissions]
  )

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={dispatch}>
        <AuthPermissionsContext.Provider value={permissionsContextValue}>{children}</AuthPermissionsContext.Provider>
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  )
}
